<template>
  <section class="project-tasks-panel">
    <button type="button" class="project-tasks-panel__header" :aria-expanded="String(expanded)" @click="expanded = !expanded">
      <span class="project-tasks-panel__heading">
        <AppIcon name="tasks" /> Tareas
        <b-badge v-if="pendingTotal !== null" variant="primary" pill>{{ pendingTotal }} {{ pendingTotal === 1 ? 'pendiente' : 'pendientes' }}</b-badge>
      </span>
      <AppIcon :name="expanded ? 'expand_less' : 'expand_more'" />
    </button>

    <b-collapse v-model="expanded">
      <div class="project-tasks-panel__content">
        <div class="project-tasks-panel__toolbar">
          <b-form-select v-model="status" size="sm" :options="statusOptions" @change="load" />
          <b-button size="sm" variant="outline-secondary" :disabled="loading" @click="load">
            <AppIcon name="refresh" :class="{ 'app-icon-spin': loading }" />
          </b-button>
          <b-button size="sm" variant="primary" class="ml-auto" @click="createTask">
            <AppIcon name="add" class="mr-1" /> Agregar
          </b-button>
        </div>

        <div v-if="loading" class="project-tasks-panel__loading"><b-spinner small /> Cargando tareas…</div>
        <div v-else-if="items.length" class="project-tasks-panel__list">
          <button v-for="item in items" :key="item.id" type="button" class="project-tasks-panel__item" @click="openTask(item)">
            <span class="project-tasks-panel__main">
              <strong>{{ item.title }}</strong>
              <small>{{ item.category || 'Sin categoría' }}</small>
            </span>
            <span class="project-tasks-panel__meta">
              <span>
                <b-avatar :src="item.assigned && item.assigned.image" :text="avatarText(staffName(item))" size="20px" />
                {{ staffName(item) }}
              </span>
              <span class="project-tasks-panel__badges">
                <b-badge :variant="statusVariant(item)">{{ statusLabel(item) }}</b-badge>
                <b-badge :variant="priorityVariant(item.priority)">{{ priorityLabel(item.priority) }}</b-badge>
              </span>
            </span>
          </button>
        </div>
        <EmptyState v-else icon="tasks" title="No hay tareas" :description="emptyDescription" />
      </div>
    </b-collapse>
  </section>
</template>

<script>
import serviceTasks from '../staff/services'
import Error from '@/handler/error'
import Funciones from '@/handler/funciones'

export default {
  name: 'ProjectTasksPanel',
  props: { projectId: { type: [Number, String], required: true } },
  data: () => ({ expanded: false, hasLoaded: false, loading: false, items: [], pendingTotal: null, status: 'pending' }),
  computed: {
    statusOptions() { return [{ value: 'pending', text: 'Pendientes' }, { value: 'completed', text: 'Completadas' }, { value: 'archived', text: 'Archivadas' }, { value: 'all', text: 'Todas' }] },
    emptyDescription() { return 'No hay tareas ' + ({ pending: 'pendientes', completed: 'completadas', archived: 'archivadas', all: '' }[this.status] || '') + ' para este proyecto.' },
  },
  mounted() { this.loadPendingTotal() },
  watch: {
    expanded(value) { if (value && !this.hasLoaded) this.load() },
    projectId() {
      this.items = []
      this.hasLoaded = false
      this.pendingTotal = null
      this.loadPendingTotal()
      if (this.expanded && this.status !== 'pending') this.load()
    },
  },
  methods: {
    load() {
      if (!this.projectId) return
      this.loading = true
      var filters = { relation_type: 'project', project_id: this.projectId }
      if (this.status === 'pending' || this.status === 'completed') {
        filters.archive_status = 'active'
        filters.completion_status = this.status
      } else if (this.status === 'archived') {
        filters.archive_status = 'archived'
      } else {
        filters.archive_status = 'all'
      }
      serviceTasks.obtenerTareas(filters).then(response => {
        this.items = Array.isArray(response.data) ? response.data : []
        this.hasLoaded = true
        if (this.status === 'pending') this.pendingTotal = this.items.length
        this.loading = false
      }).catch(error => {
        this.loading = false
        this.$awn.alert(Error.showError(error))
      })
    },
    loadPendingTotal() {
      if (!this.projectId) return
      serviceTasks.obtenerTareas({ relation_type: 'project', project_id: this.projectId, archive_status: 'active', completion_status: 'pending' }).then(response => {
        var pendingItems = Array.isArray(response.data) ? response.data : []
        this.pendingTotal = pendingItems.length
        this.expanded = pendingItems.length > 0
        if (this.status === 'pending') {
          this.items = pendingItems
          this.hasLoaded = true
        }
      }).catch(() => {})
    },
    createTask() {
      this.$router.push({ name: 'TasksStaffCrud', query: { new: '1', mode: 'project', relation_id: String(this.projectId), return_to_relation: 'project', return_relation_id: String(this.projectId) } }).catch(() => {})
    },
    openTask(item) { this.$router.push({ name: 'TasksStaffCrud', params: { taskId: String(item.id) } }).catch(() => {}) },
    staffName(item) { return item.assigned && item.assigned.name ? item.assigned.name : 'Sin asignar' },
    avatarText(name) { return Funciones.avatarFromName(name || '').text },
    statusLabel(item) { if (item.manage_status === false) return 'Sin estado'; if (item.archived) return 'Archivada'; return item.done ? 'Completada' : 'Pendiente' },
    statusVariant(item) { if (item.manage_status === false || item.archived) return 'secondary'; return item.done ? 'success' : 'warning' },
    priorityLabel(value) { return { urgent: 'Urgente', high: 'Alta', medium: 'Media', low: 'Baja' }[value] || 'Sin prioridad' },
    priorityVariant(value) { return { urgent: 'danger', high: 'warning', medium: 'primary', low: 'secondary' }[value] || 'secondary' },
  },
}
</script>

<style scoped>
.project-tasks-panel { border-top: 1px solid var(--app-color-border); margin-top: 14px; padding-top: 10px; }
.project-tasks-panel__header { align-items: center; background: transparent; border: 0; color: var(--app-color-text-secondary); display: flex; font-size: var(--app-text-sm); font-weight: 700; justify-content: space-between; padding: 0; text-align: left; width: 100%; }
.project-tasks-panel__heading { align-items: center; display: inline-flex; gap: 5px; }
.project-tasks-panel__heading .badge { font-size: 10px; font-weight: 700; margin-left: 3px; padding: 4px 7px; }
.project-tasks-panel__content { padding-top: 10px; }
.project-tasks-panel__toolbar { display: flex; gap: 6px; margin-bottom: 8px; }
.project-tasks-panel__toolbar .custom-select { max-width: 135px; }
.project-tasks-panel__loading { align-items: center; color: var(--app-color-text-muted); display: flex; font-size: var(--app-text-xs); gap: 6px; justify-content: center; min-height: 64px; }
.project-tasks-panel__list { border: 1px solid var(--app-color-border); border-radius: var(--app-radius-sm); overflow: hidden; }
.project-tasks-panel__item { align-items: center; background: var(--app-color-surface); border: 0; border-bottom: 1px solid var(--app-color-border); color: var(--app-color-text); display: flex; justify-content: space-between; padding: 8px; text-align: left; width: 100%; }
.project-tasks-panel__item:last-child { border-bottom: 0; }
.project-tasks-panel__item:hover { background: var(--app-color-primary-soft); }
.project-tasks-panel__main, .project-tasks-panel__meta { display: flex; flex-direction: column; min-width: 0; }
.project-tasks-panel__main strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.project-tasks-panel__main small { color: var(--app-color-text-muted); }
.project-tasks-panel__meta { align-items: flex-end; flex: 0 0 auto; gap: 4px; margin-left: 8px; }
.project-tasks-panel__meta > span { align-items: center; display: flex; font-size: var(--app-text-xs); gap: 4px; }
.project-tasks-panel__badges { display: flex; gap: 4px; }
.project-tasks-panel__meta .badge { font-size: 10px; }
</style>
